import React, { useCallback } from 'react';
import { Pressable, StyleSheet, ActivityIndicator } from 'react-native';
import { Text } from '@/components/common/text';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
} from 'react-native-reanimated';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { useHoverStyle } from '@/hooks/shared/useHoverStyle';
import { useStackedRow } from '@/components/shared/redesign/StackingRow';

type ButtonVariant = 'primary' | 'secondary' | 'destructive';

interface ModalButtonProps {
  label: string;
  onPress: () => void;
  variant?: ButtonVariant;
  disabled?: boolean;
  /** Whether to play the press-in scale animation. Defaults to true. */
  animateOnPress?: boolean;
  /** Show a spinner in place of the label and block presses. Keeps the button
   *  mounted at the same size so the footer never reflows between states. */
  loading?: boolean;
}

const SPRING_CONFIG = { damping: 15, stiffness: 400 };

export const ModalButton: React.FC<ModalButtonProps> = ({
  label,
  onPress,
  variant = 'primary',
  disabled = false,
  animateOnPress = true,
  loading = false,
}) => {
  const { colors, semantic, components } = useThemeColors();
  const scale = useSharedValue(1);
  // Filled variants lift (button kind); the glass secondary tints (row kind).
  // The press spring on the wrapper stays; the lift lives on the inner
  // Pressable so the two transforms never fight.
  const { hoverProps, viewStyle: hoverViewStyle } = useHoverStyle(
    variant === 'secondary' ? 'glass' : 'button',
    { disabled: disabled || loading },
  );

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  const handlePressIn = useCallback(() => {
    if (!disabled && !loading && animateOnPress) scale.value = withSpring(0.97, SPRING_CONFIG);
  }, [disabled, loading, animateOnPress, scale]);

  const handlePressOut = useCallback(() => {
    if (animateOnPress) scale.value = withSpring(1, SPRING_CONFIG);
  }, [animateOnPress, scale]);

  const stacked = useStackedRow();
  const variantStyles = {
    primary: {
      button: {
        backgroundColor: components.button.primaryBg,
      },
      label: {
        color: components.button.primaryText,
      },
    },
    secondary: {
      button: {
        backgroundColor: colors.glass.buttonBg,
        borderWidth: 1,
        borderColor: colors.glass.buttonBorder,
        // RN boxes are border-box: take the 2pt of border out of the padding
        // so this variant is exactly 44 at 1x like the unbordered ones.
        paddingVertical: 10,
      },
      label: {
        color: semantic.text.primary,
      },
    },
    destructive: {
      button: {
        backgroundColor: components.button.dangerBg,
      },
      label: {
        color: components.button.dangerText,
      },
    },
  }[variant];

  return (
    <Animated.View style={[styles.wrapper, stacked && styles.wrapperStacked, animatedStyle]}>
      <Pressable
        style={[
          styles.button,
          variantStyles.button,
          hoverViewStyle,
          disabled && styles.buttonDisabled,
        ]}
        onPress={onPress}
        onPressIn={handlePressIn}
        onPressOut={handlePressOut}
        disabled={disabled || loading}
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityState={{ disabled: disabled || loading, busy: loading }}
        {...hoverProps}
      >
        {loading ? (
          <ActivityIndicator size="small" color={variantStyles.label.color} />
        ) : (
          <Text
            numberOfLines={2}
            style={[
              styles.label,
              variantStyles.label,
              disabled && styles.labelDisabled,
            ]}
          >
            {label}
          </Text>
        )}
      </Pressable>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
  },
  // In a StackingRow that has stacked (large text) the buttons form a column:
  // a `flex: 1` wrapper measured AtMost by a max-height modal could stretch
  // to the modal or collapse to 0 depending on Yoga's stretch errata, so the
  // wrapper keeps its content height and only stretches across.
  wrapperStacked: {
    flex: 0,
    alignSelf: 'stretch',
  },
  button: {
    // Fills the flex:1 wrapper so two buttons in a footer row share the taller
    // one's height when a label wraps at large text sizes.
    flexGrow: 1,
    alignSelf: 'stretch',
    minHeight: 44,
    paddingVertical: 11,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 20,
  },
  buttonDisabled: {
    opacity: 0.45,
  },
  label: {
    fontSize: 15,
    fontWeight: '600',
    lineHeight: 22,
    textAlign: 'center',
  },
  labelDisabled: {
    opacity: 0.7,
  },
});
